/* BEGIN_COPYRIGHT_HEADER

Copyright Vspry International Limited (c) 2020
All rights reserved.

END_COPYRIGHT_HEADER */

import { useEffect } from 'react'
import styled from 'styled-components'
import { Button, FlexBox, Text, Icon, DropDown } from 'vspry-style-components'
import { useFunctionState } from 'vspry-hooks'

import { billCodeListener } from 'api/firebase'
import { getUserBillCode } from 'api/bills'

import { useLocale } from 'context/localeContext'
import { useAppConfig } from 'context/appConfigContext'
import { useAuth } from 'context/authContext'

import BillQRScanner from 'components/bills/BillQRScanner'
import PageContainer from 'components/style-elements/containers/PageContainer'
import PrettyTile from 'components/PrettyTile'

export const BillButton = styled.button<{ used?: boolean }>`
    width: 70px;
    height: 70px;
    background: ${(props) => (props.used ? props.theme.success : props.theme.buttonBG)};
    border: 2px solid ${(props) => props.theme.secondaryBG};
    border-radius: 100%;

    :hover {
        cursor: pointer;
        filter: brightness(125%);
    }
`

const CodeTile = styled.div<{ used?: boolean }>`
    width: 145px;
    height: 60px;

    background: ${(props) => (props.used ? props.theme.success : props.theme.buttonBG)};
    border-radius: 5px;
    border: none;
    display: flex;
    justify-content: center;
    align-items: center;
    z-index: 0;
`
const Step = styled.div`
    opacity: 0;
    transform: translateY(20px);
    transition: opacity 0.6s ease, transform 0.6s ease;

    &.animate {
        opacity: 1;
        transform: translateY(0);
    }
`

const init = {
    working: false,
    billCode: null as null | string,
    used: false,
    stage: null as null | 'method' | 'code' | 'scan',
}

export default function PayMerchant() {
    const { translate } = useLocale()
    const { products, currentProduct, setCurrentProduct } = useAuth()
    const { appConfig } = useAppConfig()
    const [state, setState] = useFunctionState(init)
    const { working, billCode, used, stage } = state

    const getBillCode = async () => {
        setState({ working: true })
        const res = await getUserBillCode()
        if (res) setState({ billCode: res.billCode, used: false })
        setState({ working: false })
    }

    const handleChange: Parameters<typeof billCodeListener>[0] = (doc) => {
        if (!doc.exists()) setState({ used: true })
    }
    useEffect(() => {
        if (!billCode) return () => null
        return billCodeListener(handleChange, billCode)
    }, [billCode])

    useEffect(() => {
        if (used) {
            const t = setTimeout(() => setState(init), 1000)
            return () => clearTimeout(t)
        }
        return () => null
    }, [used])

    const setCode = () => {
        getBillCode()
        setState({ stage: 'code' })
    }

    const qrScanEnabled = appConfig.consumer_bill_methods_enabled.includes('qr')
    const codeGenerationEnabled = appConfig.consumer_bill_methods_enabled.includes('code')

    const determineStepText = () => {
        if (qrScanEnabled && codeGenerationEnabled) return translate('pages.payMerchant.bothMethods')
        if (qrScanEnabled) return translate('pages.payMerchant.qrMethod')
        if (codeGenerationEnabled) return translate('pages.payMerchant.codeMethod')
        return ''
    }

    const productOptions = products.map((p) => ({ text: p.product, value: p.product }))

    return (
        <PageContainer title={translate('pages.payMerchant.title')} size='large'>
            <PrettyTile title={translate('pages.payMerchant.title')} descriptions={['Use this feature to transfer points directly to a merchant.']}>
                <FlexBox $column gap='small'>
                    <Step className='step animate'>
                        <Text size='xSmall' $left margin='no'>
                            <strong style={{ fontSize: '15px' }}>1</strong> {translate('pages.payMerchant.ensureCorrectProduct')}
                        </Text>
                    </Step>
                    <DropDown
                        id='productdropdown'
                        value={currentProduct?.product ?? null}
                        placeholder={translate('pages.payMerchant.selectProduct')}
                        options={productOptions}
                        handleChange={(value) => setCurrentProduct(products.find((p) => p.product === value) ?? null)}
                    />

                    <Step className='step animate'>
                        <Text size='xSmall' $left margin='no'>
                            <strong style={{ fontSize: '15px' }}>2</strong> {determineStepText()}
                        </Text>
                    </Step>
                </FlexBox>
                {stage === null && (
                    <>
                        {appConfig.consumer_bill_methods_enabled.includes('qr') && (
                            <Button id='pay-merchant-qr-code-btn' onClick={() => setState({ stage: 'scan' })}>
                                {translate('pages.payMerchant.scanMerchantQRCode')}
                            </Button>
                        )}
                        {appConfig.consumer_bill_methods_enabled.includes('code') && (
                            <Button id='pay-merchant-gen-code-btn' onClick={() => setCode()}>
                                {translate('pages.payMerchant.generateCode')}
                            </Button>
                        )}
                    </>
                )}

                {stage === 'code' && (
                    <FlexBox justify='center' $align gap='small'>
                        {!working && <Icon name='times' size='large' onClick={() => setState({ stage: null })} />}
                        {!working && billCode && (
                            <CodeTile id='billcode-tile' used={used}>
                                <Text id='billcode-text' $left size='xLarge' margin='no' $bold>
                                    {billCode}
                                </Text>
                            </CodeTile>
                        )}

                        <Icon name='refresh' loading={working} size='big' fitted onClick={getBillCode} />
                    </FlexBox>
                )}

                {stage === 'scan' && <BillQRScanner onClose={() => setState({ stage: null })} />}
            </PrettyTile>
        </PageContainer>
    )
}
